import {ApiClient as Api} from '@hconnect/apiclient'
import {useMutation, UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'

import {prepareJobsToBeDoneInsightUpdateFormData} from '../mappers'
import {JobsToBeDoneInsightUpdateDto, JobsToBeDoneInsightDetails} from '../types'

import {JOBS_TO_BE_DONE_INSIGHTS_QUERY_KEY} from './useGetJobsToBeDoneInsights'
import {JobsToBeDoneQueryKey} from './useJobsToBeDone'

interface UseUpdateJobsToBeDoneInsightParams {
  plantUpmId: string
  jobToBeDoneId: string
  activityId: string
  activityTaskId: string
  insightId: string
}

type UpdateJobsToBeDoneInsightOptions = UseMutationOptions<
  JobsToBeDoneInsightDetails,
  AxiosError,
  JobsToBeDoneInsightUpdateDto
>

export const useUpdateJobsToBeDoneInsight = (
  {
    plantUpmId,
    jobToBeDoneId,
    activityId,
    activityTaskId,
    insightId
  }: UseUpdateJobsToBeDoneInsightParams,
  options?: UpdateJobsToBeDoneInsightOptions
) => {
  const queryClient = useQueryClient()
  const path = `/jobs-to-be-done/api/${plantUpmId}/jobs-to-be-done/${jobToBeDoneId}/activities/${activityId}/tasks/${activityTaskId}/insights/${insightId}`
  const {onSuccess, onError, ...restOptions} = options ?? {}

  return useMutation<JobsToBeDoneInsightDetails, AxiosError, JobsToBeDoneInsightUpdateDto>({
    mutationFn: async (data) => {
      const formData = await prepareJobsToBeDoneInsightUpdateFormData(data)

      const {data: response} = await Api.axiosInstance.put<JobsToBeDoneInsightDetails>(
        path,
        formData
      )
      return response
    },
    onError: (error, variables, context, mutation) => {
      onError?.(error, variables, context, mutation)
    },
    onSuccess: (data, variables, context, mutation) => {
      void queryClient.invalidateQueries({queryKey: [JobsToBeDoneQueryKey]})
      void queryClient.invalidateQueries({queryKey: [JOBS_TO_BE_DONE_INSIGHTS_QUERY_KEY]})
      onSuccess?.(data, variables, context, mutation)
    },
    ...restOptions
  })
}
